import {
  buildLateCancellationStrikeEntries,
} from './lateCancellationStrikes';

const LA_TIME_ZONE = 'America/Los_Angeles';
const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

const getDeviceTimeZone = () => (
  Intl.DateTimeFormat().resolvedOptions().timeZone || LA_TIME_ZONE
);

const getZonedDateParts = (dateMs, timeZone) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(new Date(dateMs));
  const getPart = (type) => Number(parts.find((part) => part.type === type)?.value || 0);

  return {
    year: getPart('year'),
    month: getPart('month'),
    day: getPart('day'),
    hour: getPart('hour'),
    minute: getPart('minute'),
    second: getPart('second'),
  };
};

const getTimeZoneOffsetMs = (dateMs, timeZone) => {
  const parts = getZonedDateParts(dateMs, timeZone);
  const asUtcMs = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );

  return asUtcMs - dateMs;
};

const zonedPartsToUtcMs = (parts, timeZone) => {
  const wallClockUtcMs = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second || 0
  );
  const firstPassOffset = getTimeZoneOffsetMs(wallClockUtcMs, timeZone);
  const firstPassUtcMs = wallClockUtcMs - firstPassOffset;
  const secondPassOffset = getTimeZoneOffsetMs(firstPassUtcMs, timeZone);

  return wallClockUtcMs - secondPassOffset;
};

export const getClassDateInInstructorLocalWallClock = ({
  classDateMs,
  localTimeZone = getDeviceTimeZone(),
} = {}) => {
  const classDateNumber = Number(classDateMs);
  if (!Number.isFinite(classDateNumber)) return NaN;

  return zonedPartsToUtcMs(
    getZonedDateParts(classDateNumber, LA_TIME_ZONE),
    localTimeZone
  );
};

export const formatClassDateTimeLA = (dateMs) => {
  if (!dateMs) return 'Date unavailable';

  return new Intl.DateTimeFormat('en-US', {
    timeZone: LA_TIME_ZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(dateMs));
};

export const getClassDurationMinutes = (classData = {}) => {
  const start = Number(classData.Date);
  const end = Number(classData.EndDate);

  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;

  return Math.round((end - start) / 60000);
};

export const getClassDisplayText = (value, fallback = 'Not provided') => {
  if (value === null || value === undefined || value === '') return fallback;

  if (Array.isArray(value)) {
    const parts = value
      .map((item) => getClassDisplayText(item, ''))
      .filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : fallback;
  }

  if (['string', 'number', 'boolean'].includes(typeof value)) {
    return String(value);
  }

  if (value instanceof Date) {
    return value.toLocaleString();
  }

  if (typeof value === 'object') {
    const displayCandidate = value.title
      || value.label
      || value.name
      || value.value
      || value.FacilityName
      || value.ClassName
      || [value.FirstName, value.LastName].filter(Boolean).join(' ');

    if (displayCandidate) return getClassDisplayText(displayCandidate, fallback);
  }

  return fallback;
};

export const buildClassCompletionUpdate = ({
  drove,
  people,
  expected,
  feedback,
  concern,
}) => {
  const update = {
    DidTeachClass: Boolean(drove),
    status: drove ? 1 : 2,
  };

  if (drove) {
    update.People = people || '0';
    if (expected === 'no' && concern) update.Expected = concern;
    update.Feedback = feedback || '';
  }

  return update;
};

export const getNeedSubOutcome = ({
  classDateMs,
  nowMs = Date.now(),
  localTimeZone = getDeviceTimeZone(),
}) => {
  const localWallClockClassDateMs = getClassDateInInstructorLocalWallClock({
    classDateMs,
    localTimeZone,
  });
  const minutesUntilClass = Math.round((localWallClockClassDateMs - Number(nowMs)) / 60000);

  return {
    canDrop: localWallClockClassDateMs - Number(nowMs) > TWO_HOURS_MS,
    minutesUntilClass,
  };
};

export const isInstructorClassCompleted = (classData = {}) => (
  ['1', '2'].includes(String(classData.status))
  || String(classData.classStatus) === '6'
  || classData.Completed === true
);

export const getInstructorClassActionState = ({
  classData = {},
  nowMs = Date.now(),
  isAssignedToCurrentInstructor = true,
  localTimeZone = getDeviceTimeZone(),
}) => {
  const classDateMs = Number(classData.Date);
  const classEndMs = Number(classData.EndDate || classData.Date);
  const minutesUntilClass = Math.round((classDateMs - Number(nowMs)) / 60000);
  const isCompleted = isInstructorClassCompleted(classData);
  const isPastClass = Number.isFinite(classEndMs) && classEndMs < Number(nowMs);
  const assignedInstructorId = String(classData.AssignedInstructorID || '').trim();

  if (!isAssignedToCurrentInstructor) {
    return {
      isCompleted,
      isPastClass,
      canGetPaid: false,
      canRequestSub: false,
      requiresFacilityCallBeforeDrop: false,
      minutesUntilClass,
      reason: assignedInstructorId ? 'assigned-to-other-instructor' : 'unassigned-opportunity',
      isOpportunity: !assignedInstructorId,
    };
  }

  if (isCompleted) {
    return {
      isCompleted,
      isPastClass,
      canGetPaid: false,
      canRequestSub: false,
      requiresFacilityCallBeforeDrop: false,
      minutesUntilClass,
      reason: 'completed',
    };
  }

  if (isPastClass) {
    return {
      isCompleted,
      isPastClass,
      canGetPaid: true,
      canRequestSub: false,
      requiresFacilityCallBeforeDrop: false,
      minutesUntilClass,
      reason: 'past',
    };
  }

  const needSubOutcome = getNeedSubOutcome({ classDateMs, nowMs, localTimeZone });

  if (needSubOutcome.canDrop) {
    return {
      isCompleted,
      isPastClass,
      canGetPaid: false,
      canRequestSub: true,
      requiresFacilityCallBeforeDrop: false,
      minutesUntilClass: needSubOutcome.minutesUntilClass,
      reason: 'future-drop-allowed',
    };
  }

  return {
    isCompleted,
    isPastClass,
    canGetPaid: false,
    canRequestSub: true,
    requiresFacilityCallBeforeDrop: true,
    minutesUntilClass: needSubOutcome.minutesUntilClass,
    reason: 'within-two-hours',
  };
};

export const getNeedSubWithinTwoHoursMessage = ({
  facilityName,
  facilityPhone,
}) => {
  const name = facilityName || 'the facility';
  const phonePart = facilityPhone ? ` at ${facilityPhone}` : '';

  return `This class starts within two hours. Please call ${name}${phonePart} before requesting a sub.`;
};

export const buildDropClassUpdate = ({ isWithinTwoHours = false } = {}) => (
  isWithinTwoHours
    ? { classStatus: 4 }
    : {
      AssignedInstructorID: '',
      classStatus: 3,
    }
);

export const buildLateCanceledClassEntries = ({
  classIds = [],
  selectedDropClasses = [],
  reason = '',
  reasonOther = '',
  cancellationTime = Date.now(),
  nowMs = cancellationTime,
  existingStrikeCount = 0,
  localTimeZone,
} = {}) => {
  if (Array.isArray(selectedDropClasses) && selectedDropClasses.length > 0) {
    return buildLateCancellationStrikeEntries({
      selectedDropClasses,
      reason,
      reasonOther,
      cancellationTime,
      nowMs,
      existingStrikeCount,
      localTimeZone,
    });
  }

  return classIds.map((classId) => ({
    CancellationReason: reason || '',
    CancellationReasonOther: reason === 'Other' ? reasonOther || '' : '',
    CancellationTime: cancellationTime,
    ClassID: classId,
  }));
};

export const getDropClassCandidates = ({
  currentClass,
  seriesClasses = [],
  instructorId,
  nowMs = Date.now(),
} = {}) => {
  const repeatIdentifier = currentClass?.RepeatClassIdentifier;
  const sourceClasses = repeatIdentifier ? seriesClasses : [currentClass].filter(Boolean);
  const seen = new Set();

  return sourceClasses
    .filter((classData) => {
      const classId = classData?.id;
      const dateMs = Number(classData?.Date || 0);
      const assignedInstructorId = String(classData?.AssignedInstructorID || '').trim();

      if (!classId || seen.has(classId)) return false;
      if (assignedInstructorId !== String(instructorId || '').trim()) return false;
      if (!Number.isFinite(dateMs) || dateMs < Number(nowMs)) return false;

      seen.add(classId);
      return true;
    })
    .sort((a, b) => Number(a?.Date || 0) - Number(b?.Date || 0));
};

export const buildClaimClassUpdate = (instructorId) => ({
  AssignedInstructorID: instructorId,
});
